import { Box, Button, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import LockClockIcon from '@mui/icons-material/LockClock'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSubscription } from '@/hooks/useSubscription'

/** Show the slim trial countdown only once the trial enters its final week. */
const COUNTDOWN_THRESHOLD_DAYS = 7

/**
 * Subscription banner mounted in the researcher shell (respondent routes never
 * render it). Two states:
 *  - expired: persistent banner naming what stopped and what did not, with a
 *    way through to the plans;
 *  - countdown: slim hint during the trial's final week.
 *
 * Both editions see both states. This used to skip FaceWise entirely, on the
 * rule that the edition had no clock to run out — that rule is gone. Signing up
 * now starts a fourteen-day trial of FaceWise, so the edition that was switched
 * off here is the one most likely to be reading this banner.
 */
export default function TrialExpiredBanner() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const { isExpired, plan, daysLeft } = useSubscription()

  const showCountdown =
    !isExpired && plan === 'trial' && daysLeft !== null && daysLeft <= COUNTDOWN_THRESHOLD_DAYS

  if (!isExpired && !showCountdown) return null

  if (showCountdown) {
    return (
      <Box
        sx={{
          mx: { xs: '20px', md: '32px' },
          mt: { xs: '16px', md: '20px' },
          px: '18px',
          py: '10px',
          borderRadius: '14px',
          bgcolor: alpha(c.warning, 0.12),
          border: `1px solid ${alpha(c.warning, 0.35)}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <HourglassBottomIcon sx={{ fontSize: 18, color: c.warning }} />
        <Typography sx={{ fontSize: 13.5, flex: 1, minWidth: 0 }}>
          {t('trial.daysLeft', { count: daysLeft })}
        </Typography>
        <Button
          size="small"
          onClick={() => navigate('/plans')}
          sx={{ flexShrink: 0, fontWeight: 700, fontSize: 13 }}
        >
          {t('trial.cta')}
        </Button>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        mx: { xs: '20px', md: '32px' },
        mt: { xs: '16px', md: '20px' },
        background: theme.affexy.gradients.ctaCard,
        color: c.white,
        borderRadius: '18px',
        p: '18px 22px',
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '14px',
          bgcolor: alpha(c.coral, 0.22),
          display: 'grid',
          placeItems: 'center',
          flex: '0 0 44px',
        }}
      >
        <LockClockIcon sx={{ fontSize: 22, color: c.coral }} />
      </Box>
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 16 }}>
          {plan === 'full' ? t('trial.subscriptionExpiredTitle') : t('trial.expiredTitle')}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: alpha(c.white, 0.72) }}>
          {t('trial.expiredBody')}
        </Typography>
      </Stack>
      <Button
        variant="contained"
        onClick={() => navigate('/plans')}
        endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
        sx={{ flexShrink: 0, borderRadius: 34, px: 2.5, py: 1, fontSize: 14, fontWeight: 700 }}
      >
        {t('trial.cta')}
      </Button>
    </Box>
  )
}
